import type { ResolutionString } from "@/charting_library/charting_library";
import type { Datasets, Subcharts } from "../../../types";
import type { LibraryChartState } from "../types";
import { isPanelWithCustomIndicator } from "./studies-helpers";

export const updateSymbolInState = (
	state: LibraryChartState,
	ticker: string,
	name: string,
) => {
	const newState = { ...state };
	for (const chart of newState.charts) {
		for (const pane of chart.panes) {
			for (const source of pane.sources) {
				if (source.type === "MainSeries") {
					source.state.symbol = ticker;
					source.state.shortName = name;
					return newState;
				}
			}
		}
	}
	return newState;
};

export const updateIntervalInState = (
	state: LibraryChartState,
	interval: ResolutionString,
) => {
	const newState = { ...state };
	for (const chart of newState.charts) {
		for (const pane of chart.panes) {
			for (const source of pane.sources) {
				if (source.type === "MainSeries") {
					source.state.interval = interval;
					return newState;
				}
			}
		}
	}
	return newState;
};

export const filterStudiesByPanelValues = (
	panels: (Subcharts | Datasets)[] | undefined,
	chartState: Partial<LibraryChartState>,
): Partial<LibraryChartState> => {
	if (!chartState.charts?.length) return chartState;

	const updatedPanes = chartState.charts[0].panes.filter((pane) => {
		const paneName = pane.sources[0].metaInfo?.name;
		const paneDescription = pane.sources[0].metaInfo?.description;

		return isPanelWithCustomIndicator(paneDescription)
			? panels?.some((panel) => paneName === panel || paneDescription === panel)
			: true;
	});

	for (const pane of updatedPanes) {
		for (const source of pane.sources) {
			if (source.metaInfo) {
				source.metaInfo.inputs = source.metaInfo.inputs ?? [];
				source.state.inputs = source.state.inputs ?? {};
			}
		}
	}

	const updatedState = { ...chartState };
	updatedState.charts![0].panes = updatedPanes;

	return updatedState;
};

export const removeAllShapes = (
	chartState: Partial<LibraryChartState>,
): Partial<LibraryChartState> => {
	if (!chartState.charts?.length) return chartState;

	const updatedState = { ...chartState };

	for (const pane of updatedState.charts![0].panes) {
		pane.sources = pane.sources.filter((source) => {
			const sourceType = source.type.toLowerCase();
			return (
				sourceType.startsWith("study") || sourceType.startsWith("mainseries")
			);
		});
	}

	return updatedState;
};
